<template>
  <div class="px-page mb-8">
    <div class="flex items-center justify-between">
      <h2 class="text-xl font-semibold leading-tight"> Order {{ order_number }} </h2>
      <div class="space-x-2">
        <button v-if="isManagerMode" class="btn btn-primary" @click="exitManagerMode">
          <EyeSlashIcon class="h-5 w-5" />
          <span class="ml-2 hidden md:block">End Manager Mode</span>
        </button>
        <Link as="button" :href="$route('qa.reset', id)" class="btn btn-danger">
          <ArrowPathIcon class="h-5 w-5" />
          <span class="ml-2 hidden md:block">Reset</span>
        </Link>
      </div>
    </div>
  </div>

  <div class="px-page py-4 sm:py-6">
    <div class="flex items-center justify-between">
      <h3 class="text-lg font-semibold leading-tight"> Completed Items </h3>
    </div>
  </div>

  <div class="card">
    <div class="divide-y divide-gray-200">
      <template v-for="item in items" :key="item.sku">
        <Item v-if="item.status === 'Complete'" :item="item" />
      </template>
    </div>
  </div>

  <div class="px-page py-4 sm:py-6">
    <div class="flex items-center justify-between">
      <h3 class="text-lg font-semibold leading-tight"> Bundles </h3>
    </div>
  </div>

  <template v-for="bundle in bundles" :key="bundle.id">
    <div class="mb-6 md:mb-12">
      <div
        :class="{
          'border-warning-400 bg-warning-50': bundle.status === 'Incomplete',
          'border-success-400 bg-success-50': bundle.status === 'Complete',
        }"
        class="px-page border-l-4 py-4 sm:py-6">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-semibold leading-tight">
            {{ bundle.title }}
          </h3>
          <div class="space-x-2">
            <div v-if="bundle.apply_grip" class="inline-flex items-center rounded-md border border-primary-500 bg-primary-50 px-2.5 py-0.5 text-sm font-medium text-primary-700"> Apply Grip </div>
            <div v-if="!bundle.apply_grip && bundle.title.toLowerCase().includes('complete')" class="inline-flex items-center rounded-md border border-danger-500 bg-danger-50 px-2.5 py-0.5 text-sm font-medium text-danger-700"> DO NOT Apply Grip </div>
            <div v-if="bundle.assemble_board" class="inline-flex items-center rounded-md border border-primary-500 bg-primary-50 px-2.5 py-0.5 text-sm font-medium text-primary-700"> Assemble Board </div>
            <div v-if="!bundle.assemble_board && bundle.title.toLowerCase().includes('complete')" class="inline-flex items-center rounded-md border border-danger-500 bg-danger-50 px-2.5 py-0.5 text-sm font-medium text-danger-700"> DO NOT Assemble Board </div>
          </div>
        </div>
      </div>

      <div v-show="bundle.status !== 'Complete'" class="card">
        <Item v-for="item in bundle.items" :key="item.sku" :item="item" @click="completeItem(item.sku, item.overrideable)" />
      </div>
    </div>
  </template>

  <div v-if="items.length" class="mb-12">
    <div class="px-page py-4 sm:py-6">
      <div class="flex items-center justify-between">
        <h3 class="text-lg font-semibold leading-tight"> Items </h3>
      </div>
    </div>

    <div class="card">
      <div class="divide-y divide-gray-200">
        <template v-for="item in items" :key="item.sku">
          <Item v-if="item.status !== 'Complete'" :item="item" @click="completeItem(item.sku, item.overrideable)" />
        </template>
      </div>
    </div>
  </div>

  <TransitionRoot as="template" :show="isQaTrigger">
    <Dialog as="div" static :open="isQaTrigger" class="relative z-10" @close="() => null">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="pointer-events-none fixed inset-0 bg-primary-300 bg-opacity-50 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div class="bg-white px-4 pb-4 pt-5 sm:p-8 sm:pb-6">
                <div class="flex items-start">
                  <div class="mx-0 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-100">
                    <QuestionMarkCircleIcon class="h-6 w-6 text-primary-600" aria-hidden="true" />
                  </div>
                  <div class="mt-6 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <DialogTitle as="h3" class="text-xl font-medium leading-6 text-gray-900">{{ currentTrigger?.title }} </DialogTitle>
                    <div class="mt-4">
                      <p class="text-lg text-gray-500">{{ currentTrigger?.message }}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="bg-gray-50 px-4 py-4 sm:flex sm:flex-row-reverse sm:px-8">
                <Link v-if="currentTrigger?.reject" as="button" :href="$route('qa.reset', id)" class="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"> Ooops </Link>
                <button type="button" class="mt-4 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:ml-3 sm:mt-0 sm:w-auto sm:text-sm" @click="completeTrigger(currentTrigger)"> Yes </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <TransitionRoot as="template" :show="isQaError">
    <Dialog as="div" class="relative z-10" @close="isQaError = false">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-red-300 bg-opacity-50 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-8">
              <div>
                <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                  <button type="button" class="focus:outline-none" @click="isQaSuccess = false">
                    <ExclamationTriangleIcon class="h-6 w-6 text-red-600" aria-hidden="true" />
                  </button>
                </div>
                <div class="mt-6 text-center sm:mt-5">
                  <DialogTitle as="h3" class="text-xl font-medium leading-6 text-gray-900">Uh-Oh!</DialogTitle>
                  <div class="mt-4">
                    <p class="text-lg text-gray-500">{{ $page.props.flash.qaError }}</p>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <TransitionRoot as="template" :show="isQaSuccess">
    <Dialog as="div" class="relative z-10" @close="isQaSuccess = false">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-green-300 bg-opacity-50 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-8">
              <div>
                <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <button type="button" class="focus:outline-none" @click="isQaSuccess = false">
                    <CheckIcon class="h-6 w-6 text-green-600" aria-hidden="true" />
                  </button>
                </div>
                <div class="mt-6 text-center sm:mt-5">
                  <DialogTitle as="h3" class="text-xl font-medium leading-6 text-gray-900">Alright!</DialogTitle>
                  <div class="mt-4">
                    <p class="text-lg text-gray-500">{{ $page.props.flash.qaSuccess }}</p>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { ArrowPathIcon, CheckIcon, ExclamationTriangleIcon, EyeSlashIcon, QuestionMarkCircleIcon } from "@heroicons/vue/24/outline";
import { Link, router, usePage } from "@inertiajs/vue3";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

import Item from "./Item.vue";
import { useBarcodeScanner } from "../../composables/BarcodeScanner";

// barcode scanner composable
useBarcodeScanner();

// add event listener when entering page
onMounted(() => {
  document.addEventListener("scanComplete", handleScan);
});

// remove event listener when leaving page
onUnmounted(() => {
  document.removeEventListener("scanComplete", handleScan);
});

// setup constants
const { id, order_number, items } = defineProps(["id", "order_number", "items"]);

const bundles = computed(() => usePage().props.bundles);
const currentBundle = computed(() => {
  const current = bundles.value.find((bundle) => {
    return bundle.current;
  });

  return current ?? null;
});

const triggers = computed(() => usePage().props.triggers);
const currentTrigger = computed(() =>
  triggers.value.find((trigger) => {
    if (currentBundle.value) {
      return trigger.bundle_id === currentBundle.value.id && currentBundle.value.status === "has-triggers" && !trigger.complete;
    } else {
      return usePage().props.status === "has-triggers" && !trigger.complete;
    }
  })
);

// QA triggers
const isQaTrigger = computed(() => !!currentTrigger.value);

// Manager Mode
const notProduction = process.env.MIX_APP_ENV !== "production";
const isManagerMode = ref(notProduction);

// QA feedback
let isQaError = ref(false);
let isQaSuccess = ref(!!usePage().props.flash.qaSuccess);

if (isQaSuccess.value) {
  setTimeout(() => {
    router.get(route("qa.index"));
  }, 2000);
}

// Watcher for QA error
watch(
  () => usePage().props.flash.qaError,
  (error) => {
    if (error) {
      isQaError.value = true;

      setTimeout(() => (isQaError.value = false), 2000);
    }
  },
  { deep: true }
);

// Watcher for QA Success
watch(
  () => usePage().props.flash.qaSuccess,
  (success) => {
    if (success) {
      isQaSuccess.value = true;

      setTimeout(() => {
        router.get(route("qa.index"));
      }, 2000);
    }
  },
  { deep: true }
);

// Complete a QA item
const completeItem = (item, bypass) => {
  if (isManagerMode.value || bypass) {
    router.post(
      route("qa.complete-item", id),
      {
        item: item,
        bundle: currentBundle.value ? currentBundle.value.id : null,
      },
      {
        preserveScroll: true,
      }
    );
  }
};

// Complete a QA trigger
const completeTrigger = (trigger) => {
  router.post(
    route("qa.complete-trigger", id),
    {
      trigger: trigger,
    },
    {
      preserveScroll: true,
    }
  );
};

// Cancel Manager Mode
const exitManagerMode = () => {
  isManagerMode.value = false;
};

// Handle scan complete event
const handleScan = ({ detail }) => {
  if (detail === "manager") {
    isManagerMode.value = true;
  } else {
    completeItem(detail, true);
  }
};
</script>
